package com.eladfinish.bible.contest.screens.search.menu

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Share
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.all_the_bible
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.copy_search_result
import com.eladfinish.bible.contest.copy_text
import com.eladfinish.bible.contest.export_to_file
import com.eladfinish.bible.contest.first_last_letters_in_name
import com.eladfinish.bible.contest.help
import com.eladfinish.bible.contest.ic_chart_bar_stacked_black
import com.eladfinish.bible.contest.ic_help
import com.eladfinish.bible.contest.lefi_hamikud
import com.eladfinish.bible.contest.navigation.SearchScreenNavigationDestination
import com.eladfinish.bible.contest.paste_text
import com.eladfinish.bible.contest.pasuk_lehazmana
import com.eladfinish.bible.contest.regular_search
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import com.eladfinish.bible.contest.screens.search.model.SearchPlace
import com.eladfinish.bible.contest.search_history
import com.eladfinish.bible.contest.search_mode
import com.eladfinish.bible.contest.search_popular_searches
import com.eladfinish.bible.contest.select_all_text
import com.eladfinish.bible.contest.share_search
import com.eladfinish.bible.contest.stats
import com.eladfinish.bible.contest.text_only_qri
import com.eladfinish.bible.contest.text_with_nikud
import com.eladfinish.bible.contest.text_with_teamim
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.toolbar.ConfigurableToolbar
import com.eladfinish.bible.contest.ui.components.toolbar.SetToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.ToolbarPreviewOverrides
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.SubMenuContent
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionPlacement
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarHeaderComponentConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarIcon
import com.eladfinish.bible.contest.ui.components.toolbar.model.ToolbarKind
import com.eladfinish.bible.contest.ui.icons.AppIcons
import com.eladfinish.bible.contest.ui.menus.actions.SearchToolbarAction
import com.eladfinish.bible.contest.ui.menus.actions.ToolbarActionEmitter
import com.eladfinish.bible.contest.where_to_search
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.persistentListOf

private val searchToolbarOrder = listOfNotNull(
    // Toolbar
    SearchToolbarId.HELP,
    SearchToolbarId.HISTORY,

    // Overflow
    SearchToolbarId.POPULAR_SEARCHES,
    SearchToolbarId.SELECT_ALL,
    SearchToolbarId.COPY,
    SearchToolbarId.PASTE,
    SearchToolbarId.EXPORT,
    SearchToolbarId.COPY_ALL_RESULTS,
    SearchToolbarId.ONLY_QRI,
    SearchToolbarId.TEXT_WITH_NIKUD,
    SearchToolbarId.TEXT_WITH_TEAMIM,
    SearchToolbarId.STATS,
    SearchToolbarId.SEARCH_MODE_MENU,
    SearchToolbarId.WHERE_TO_SEARCH_MENU,
    SearchToolbarId.SHARE_SEARCH,
)

enum class SearchToolbarId {
    HELP,
    HISTORY,
    POPULAR_SEARCHES,
    SELECT_ALL,
    COPY,
    PASTE,
    EXPORT,
    STATS,
    SHARE_SEARCH,
    COPY_ALL_RESULTS,
    SEARCH_MODE_MENU,
    WHERE_TO_SEARCH_MENU,
    TEXT_WITH_NIKUD,
    TEXT_WITH_TEAMIM,
    ONLY_QRI,
}

@Composable
internal fun SearchMenu(
    title: String,
    toolbarEmitter: ToolbarActionEmitter<SearchToolbarAction>,
    searchModeSelected: SearchMode,
    whereToSearchSelected: SearchPlace,
) {
    SetToolbarConfiguration(
        configuration = buildSearchToolbarConfiguration(
            title = title,
            toolbarEmitter = toolbarEmitter,
            searchModeSelected = searchModeSelected,
            whereToSearchSelected = whereToSearchSelected,
        )
    )
}

@Composable
private fun createSearchToolbarAction(
    id: SearchToolbarId,
    toolbarEmitter: ToolbarActionEmitter<SearchToolbarAction>,
    searchModeSelected: SearchMode,
    whereToSearchSelected: SearchPlace,
    textWithNikud: Boolean,
    textWithTeamim: Boolean,
    onlyQri: Boolean,
): ToolbarActionConfig<SearchToolbarId> =
    when (id) {
        SearchToolbarId.HELP ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.PainterIcon(painterResource(SharedRes.images.ic_help)),
                menuText = SharedRes.strings.help.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.Help) },
                placement = ToolbarActionPlacement.OnToolbar,
            )

        SearchToolbarId.HISTORY ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.VectorIcon(AppIcons.History),
                menuText = SharedRes.strings.search_history.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.History) },
                placement = ToolbarActionPlacement.OnToolbar,
            )

        SearchToolbarId.POPULAR_SEARCHES ->
            ToolbarActionConfig.TextActionRes(
                id = id,
                stringDesc = SharedRes.strings.search_popular_searches.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.PopularSearches) },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.SELECT_ALL ->
            ToolbarActionConfig.TextActionRes(
                id = id,
                stringDesc = SharedRes.strings.select_all_text.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.SelectAll) },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.COPY ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.VectorIcon(AppIcons.ContentCopy),
                menuText = SharedRes.strings.copy_text.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.Copy) },
                placement = ToolbarActionPlacement.InOverflow,
                iconColor = Color.Black,
            )

        SearchToolbarId.PASTE ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.VectorIcon(AppIcons.ContentPaste),
                menuText = SharedRes.strings.paste_text.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.Paste) },
                placement = ToolbarActionPlacement.InOverflow,
                iconColor = Color.Black,
            )

        SearchToolbarId.EXPORT ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.VectorIcon(AppIcons.ImportExport),
                menuText = SharedRes.strings.export_to_file.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.Export) },
                placement = ToolbarActionPlacement.InOverflow,
                iconColor = Color.Black,
            )

        SearchToolbarId.STATS ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.PainterIcon(
                    painterResource(SharedRes.images.ic_chart_bar_stacked_black)
                ),
                menuText = SharedRes.strings.stats.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.Stats) },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.SHARE_SEARCH ->
            ToolbarActionConfig.IconAction(
                id = id,
                icon = ToolbarIcon.VectorIcon(Icons.Default.Share),
                menuText = SharedRes.strings.share_search.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.ShareSearch) },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.COPY_ALL_RESULTS ->
            ToolbarActionConfig.TextActionRes(
                id = id,
                stringDesc = SharedRes.strings.copy_search_result.desc(),
                onClick = { toolbarEmitter.emit(SearchToolbarAction.CopyAllResults) },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.SEARCH_MODE_MENU ->
            ToolbarActionConfig.SubMenu(
                id = id,
                stringDesc = SharedRes.strings.search_mode.desc(),
                placement = ToolbarActionPlacement.InOverflow,
                content = SubMenuContent.RadioSubMenu(
                    id = id,
                    selected = searchModeSelected,
                    options = listOf(
                        SubMenuContent.RadioOption(
                            SearchMode.REGULAR_SEARCH,
                            SharedRes.strings.regular_search.desc(),
                        ),
                        SubMenuContent.RadioOption(
                            SearchMode.FIRST_LAST_LETTERS_IN_NAME,
                            SharedRes.strings.first_last_letters_in_name.desc(),
                        ),
                        SubMenuContent.RadioOption(
                            SearchMode.VERSE_FOR_INVITATION,
                            SharedRes.strings.pasuk_lehazmana.desc(),
                        ),
                    ),
                    onOptionSelected = { option ->
                        when (option) {
                            SearchMode.REGULAR_SEARCH ->
                                toolbarEmitter.emit(SearchToolbarAction.SearchMode.RegularSearch)

                            SearchMode.FIRST_LAST_LETTERS_IN_NAME ->
                                toolbarEmitter.emit(SearchToolbarAction.SearchMode.FirstLastLettersInName)

                            SearchMode.VERSE_FOR_INVITATION ->
                                toolbarEmitter.emit(SearchToolbarAction.SearchMode.PasukLeHazmana)
                        }
                    },
                ),
            )

        SearchToolbarId.WHERE_TO_SEARCH_MENU ->
            ToolbarActionConfig.SubMenu(
                id = id,
                stringDesc = SharedRes.strings.where_to_search.desc(),
                placement = ToolbarActionPlacement.InOverflow,
                content = SubMenuContent.RadioSubMenu(
                    id = id,
                    selected = whereToSearchSelected,
                    options = listOf(
                        SubMenuContent.RadioOption(
                            SearchPlace.BY_MIKUD,
                            SharedRes.strings.lefi_hamikud.desc(),
                        ),
                        SubMenuContent.RadioOption(
                            SearchPlace.WHOLE_BIBLE,
                            SharedRes.strings.all_the_bible.desc(),
                        ),
                    ),
                    onOptionSelected = { option ->
                        when (option) {
                            SearchPlace.BY_MIKUD ->
                                toolbarEmitter.emit(SearchToolbarAction.WhereToSearch.ByMikud)

                            SearchPlace.WHOLE_BIBLE ->
                                toolbarEmitter.emit(SearchToolbarAction.WhereToSearch.WholeBible)
                        }
                    },
                ),
            )

        SearchToolbarId.TEXT_WITH_NIKUD,
        SearchToolbarId.TEXT_WITH_TEAMIM,
        SearchToolbarId.ONLY_QRI ->
            createSearchCheckboxAction(
                id = id,
                toolbarEmitter = toolbarEmitter,
                textWithNikud = textWithNikud,
                textWithTeamim = textWithTeamim,
                onlyQri = onlyQri,
            )
    }

internal fun createSearchCheckboxAction(
    id: SearchToolbarId,
    toolbarEmitter: ToolbarActionEmitter<SearchToolbarAction>,
    textWithNikud: Boolean,
    textWithTeamim: Boolean,
    onlyQri: Boolean,
): ToolbarActionConfig.CheckboxActionRes<SearchToolbarId> =
    when (id) {
        SearchToolbarId.TEXT_WITH_NIKUD ->
            ToolbarActionConfig.CheckboxActionRes(
                id = id,
                stringDesc = SharedRes.strings.text_with_nikud.desc(),
                checked = textWithNikud,
                onCheckedChange = {
                    toolbarEmitter.emit(SearchToolbarAction.TextWithNikud(checked = it))
                },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.TEXT_WITH_TEAMIM ->
            ToolbarActionConfig.CheckboxActionRes(
                id = id,
                stringDesc = SharedRes.strings.text_with_teamim.desc(),
                checked = textWithTeamim,
                onCheckedChange = {
                    toolbarEmitter.emit(SearchToolbarAction.TextWithTeamim(checked = it))
                },
                placement = ToolbarActionPlacement.InOverflow,
            )

        SearchToolbarId.ONLY_QRI ->
            ToolbarActionConfig.CheckboxActionRes(
                id = id,
                stringDesc = SharedRes.strings.text_only_qri.desc(),
                checked = onlyQri,
                onCheckedChange = {
                    toolbarEmitter.emit(SearchToolbarAction.TextOnlyQri(checked = it))
                },
                placement = ToolbarActionPlacement.InOverflow,
            )

        else -> error("$id is not a checkbox action")
    }

@Composable
private fun buildSearchToolbarConfiguration(
    title: String,
    toolbarEmitter: ToolbarActionEmitter<SearchToolbarAction>,
    searchModeSelected: SearchMode,
    whereToSearchSelected: SearchPlace,
    textWithNikud: Boolean = true,
    textWithTeamim: Boolean = true,
    onlyQri: Boolean = false,
): ToolbarConfiguration<SearchToolbarId> {

    val actions = searchToolbarOrder.map {
        createSearchToolbarAction(
            id = it,
            toolbarEmitter = toolbarEmitter,
            searchModeSelected = searchModeSelected,
            whereToSearchSelected = whereToSearchSelected,
            textWithNikud = textWithNikud,
            textWithTeamim = textWithTeamim,
            onlyQri = onlyQri,
        )
    }

    return ToolbarConfiguration(
        kind = ToolbarKind.Search,
        title = ToolbarHeaderComponentConfig.Text(text = title),
        actions = persistentListOf(*actions.toTypedArray()),
    )
}

@Preview(showBackground = true)
@Composable
private fun SearchMenuPreview() {
    val emitter = ToolbarActionEmitter<SearchToolbarAction> {}
    val config = buildSearchToolbarConfiguration(
        title = SearchScreenNavigationDestination().title,
        toolbarEmitter = emitter,
        searchModeSelected = SearchMode.REGULAR_SEARCH,
        whereToSearchSelected = SearchPlace.WHOLE_BIBLE,
    )
    PreviewTheme {
        ConfigurableToolbar(
            configuration = config,
            hebrewGender = HebrewGender.MALE,
            isDebug = true,
        )
    }
}

@Suppress("VisibleForTests")
@Preview(showBackground = true, heightDp = 500)
@Composable
private fun SearchMenuOverflowPreview() {
    val emitter = ToolbarActionEmitter<SearchToolbarAction> {}
    val config = buildSearchToolbarConfiguration(
        title = SearchScreenNavigationDestination().title,
        toolbarEmitter = emitter,
        searchModeSelected = SearchMode.REGULAR_SEARCH,
        whereToSearchSelected = SearchPlace.WHOLE_BIBLE,
    )

    PreviewTheme {
        ConfigurableToolbar(
            configuration = config,
            hebrewGender = HebrewGender.MALE,
            isDebug = true,
            previewOverrides = ToolbarPreviewOverrides(
                overflowExpanded = true,
            )
        )
    }
}
